import React, { useState } from 'react';
import axios from 'axios';
import configurations from "../../../configurations";
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Grid from '@material-ui/core/Grid';
import AddAdjustmentFeeDialog from './AddAdjustmentFeeDialog';
import PaymentConfirmation from './ConfirmBox';
import { patterns, errorMessage } from './CreatePayment';
import { getTransactionIdValue, getNotesDate } from '../../Services/CommonServices';

const AddAdjustmentFee = (props) => {

    const [transactionId, setTransactionId] = useState('');
    const [transactionIdError, setTransactionIdError] = useState(false);
    const [showAdjustmentFeeDialog, toggleAdjustmentFeeDialog] = useState(false);
    const [showTransactionDetail, toggleTransactionTableView] = useState(false);
    const [transactionDetail, setTransactionDetail] = useState([]);
    const [selectedTransaction, setSelectedTransaction] = useState(false);
    const [feeRequest, setFeeRequest] = useState({});
    const [showConfirm, toggleConfirm] = useState(false);

    const setTransactionIdValue = (e) => {
        const value = e.target.value.trim();
        const isPatternMatched = value.match(patterns.amount);
        setTransactionIdError(!isPatternMatched);
        setTransactionId(value);
    };

    const getTransactionDetails = () => {
        if (transactionId.length <= 0) {
            return false;
        }
        props.toggleLoader(true);
        setTransactionDetail([]);
        toggleTransactionTableView(false);
        const apiURL = configurations.paymentBaseUrl + 'transaction/transactionHistory?searchKey=transactionId&searchValue=' + transactionId;

        axios.get(apiURL).then((resp) => {
            if (resp && resp.data.code == 200) {
                if (resp.data.response.length > 0) {
                    const transactionList = resp.data.response.filter(
                        (item) => item.source == props.memberDetails.memberIdSource
                    );
                    if (transactionList.length > 0) {
                        setTransactionDetail(transactionList);
                        toggleTransactionTableView(true);
                    } else {
                        props.setAlertMsg('Transaction details not found!');
                        props.toggleAlert(true);
                    }
                } else {
                    props.setAlertMsg('Transaction details not found!');
                    props.toggleAlert(true);
                }
            }
            props.toggleLoader(false);
        }).catch(() => {
            props.setAlertMsg(errorMessage);
            props.toggleAlert(true);
            props.toggleLoader(false);
        });
    };

    const getTransactionMsg = (transaction) => {
        if (transaction.transactionStatus == 'FAIL') {
            let reason = transaction.refundDescription;
            if (transaction.reason) {
                try {
                    reason = JSON.parse(transaction.reason).error_message;
                } finally {
                    return reason;
                }
            }
        }
        if (transaction.reason && transaction.reason.length > 0) {
            return transaction.reason;
        }
        return transaction.refundDescription;
    };

    const getAdjustmentFeeValue = (transaction) => {
        const raw = transaction.adjustmentFee ?? transaction.adjustmentFeeAmount ?? 0;
        const value = parseFloat(raw);
        return Number.isNaN(value) ? 0 : value;
    };

    const isAdjustmentFeeTransaction = (transaction) => {
        const description = getTransactionMsg(transaction) || '';
        return description.includes('Adjustment Fee Reason');
    };

    const hasExistingAdjustmentFee = (transaction, allTransactions = []) => {
        if (isAdjustmentFeeTransaction(transaction)) {
            return true;
        }

        const hasLinkedFeePayment = allTransactions.some(
            (item) => item.referenceTransactionid == transaction.transactionId &&
                getAdjustmentFeeValue(item) > 0
        );
        if (hasLinkedFeePayment) {
            return true;
        }

        return getAdjustmentFeeValue(transaction) > 0;
    };

    const canAddAdjustmentFee = (transaction, allTransactions = []) => {
        return transaction.authorizationMessage == 'APPROVED' &&
            !hasExistingAdjustmentFee(transaction, allTransactions) &&
            !isAdjustmentFeeTransaction(transaction);
    };

    const onAddAdjustmentFeeClick = (transaction) => {
        const data = {
            transactionId: transaction.transactionId,
            affiliationId: transaction.affiliationId,
            source: transaction.source
        };
        axios.post(process.env.REACT_APP_transactionBaseUrl + '/adminportal/validateAdjustmentFee', data)
            .then(res => {
                if (res.data.code == 204) {
                    props.setAlertMsg(res.data.message);
                    props.toggleAlert(true);
                } else {
                    setSelectedTransaction(transaction);
                    toggleAdjustmentFeeDialog(true);
                }
            })
            .catch(() => {
                setSelectedTransaction(transaction);
                toggleAdjustmentFeeDialog(true);
            });
    };

    const showAddAdjustmentFeeBtn = (transaction) => {
        return (
            <Button
                variant="contained"
                color="primary"
                size="small"
                disabled={!canAddAdjustmentFee(transaction, transactionDetail)}
                onClick={() => onAddAdjustmentFeeClick(transaction)}
            >
                Add Adjustment Fee
            </Button>
        );
    };

    const getAdjustmentFeeStatus = (transaction) => {
        if (getAdjustmentFeeValue(transaction) <= 0) {
            return null;
        }
        const feeAmount = getAdjustmentFeeValue(transaction);
        const refundedAmount = parseFloat(transaction.adjustmentFeeRefundAmount || 0);
        if (refundedAmount >= feeAmount) {
            return 'Refunded';
        }
        if (refundedAmount > 0) {
            return 'Partially Refunded';
        }
        return 'Active';
    };

    const confirmAddFee = (data) => {
        toggleConfirm(true);
        setFeeRequest(data);
        toggleAdjustmentFeeDialog(false);
    };

    const makeAddAdjustmentFee = () => {
        if (!selectedTransaction) {
            return;
        }
        props.toggleLoader(true);
        const loggedinUserEmail = localStorage.getItem('email');
        const request = {
            admin: loggedinUserEmail,
            amount: parseFloat(feeRequest.adjustmentFee),
            adjustmentFee: parseFloat(feeRequest.adjustmentFee),
            applicationFee: 0,
            description: `Adjustment Fee Reason - ${feeRequest.reason} Original Transaction ID - ${selectedTransaction.transactionId}`,
            displayAmount: 0,
            merchantFee: 0,
            monthlyShare: 0,
            referenceTransactionId: selectedTransaction.transactionId,
            returnFeeAmount: 0,
            source: props.memberDetails.memberIdSource,
            uhfMonthlyFee: 0,
        };

        axios.post(configurations.paymentBaseUrl + 'adminportal/paymentBySource', request)
            .then((response) => {
                props.setAlertMsg(response.data.message);
                props.toggleAlert(true);
                addAdjustmentFeeNoteToCSR(feeRequest.reason);
                setTransactionId('');
                setTransactionDetail([]);
                toggleTransactionTableView(false);
                setSelectedTransaction(false);
            })
            .catch(() => {
                props.setAlertMsg('Request Failed!');
                props.toggleAlert(true);
                props.toggleLoader(false);
            });
    };

    const addAdjustmentFeeNoteToCSR = (reason) => {
        props.login()
            .then((token) => {
                const memberIdSource = props.memberDetails.memberIdSource;
                axios.get(configurations.csrBaseUrl + 'csrportal/getnotes?memberIdSource=' + memberIdSource, {
                    headers: { Authorization: token }
                })
                    .then((response) => {
                        if (response) {
                            addNotes(token, response.data, reason);
                        }
                    })
                    .catch(() => {
                        props.toggleLoader(false);
                    });
            });
    };

    const addNotes = (token, userData, reason) => {
        const existingNotes = userData.notes ? userData.notes : '';
        const date = getNotesDate();
        const loggedinUserEmail = localStorage.getItem('email');
        const notesRequest = {
            memberSSN: userData.memberSSN,
            csrUser: loggedinUserEmail,
            notes: 'User:' + loggedinUserEmail + ',' + 'Date:' + date +
                '\nAudit Trail: Adjustment Fee Applied - Amount: $' + feeRequest.adjustmentFee +
                ', Reason: ' + reason + '\n\n' + existingNotes,
        };
        axios.post(configurations.csrBaseUrl + 'csrportal/notes', notesRequest, {
            headers: { Authorization: token }
        })
            .then(() => {
                props.toggleLoader(false);
            })
            .catch(() => {
                props.toggleLoader(false);
            });
    };

    const transactionIdOnBlur = () => {
        getTransactionDetails();
    };

    const closeConfirm = () => {
        toggleConfirm(false);
    };

    const accept = () => {
        toggleConfirm(false);
        makeAddAdjustmentFee();
    };

    return (
        <div>
            <Grid container spacing={3}>
                <Grid item xs={6}>
                    <div style={{ textAlign: 'left', paddingLeft: '10%', display: 'flex' }}>
                        <TextField
                            disabled={props.disableAll}
                            variant="filled"
                            style={{ width: '55%' }}
                            value={transactionId}
                            label="Enter Transaction ID"
                            onChange={setTransactionIdValue}
                            required={true}
                            error={transactionIdError}
                            helperText={transactionIdError ? 'Enter a Valid Transaction ID' : ''}
                            onBlur={transactionIdOnBlur}
                            onKeyPress={(e) => {
                                if (e.key == 'Enter') {
                                    e.target.blur();
                                }
                            }}
                        />
                    </div>
                </Grid>

                <Grid item xs={12} hidden={!showTransactionDetail}>
                    <Table style={{ width: '90%' }} className="transaction-table" size="small" aria-label="adjustment fee transactions">
                        <TableHead>
                            <TableRow>
                                <TableCell align="left">Date</TableCell>
                                <TableCell>Transaction ID</TableCell>
                                <TableCell align="left">Type</TableCell>
                                <TableCell align="left">Payment Type</TableCell>
                                <TableCell align="left">Amount</TableCell>
                                <TableCell align="left">Authorization</TableCell>
                                <TableCell align="left">Month</TableCell>
                                <TableCell align="left" style={{ maxWidth: '300px' }}>Description</TableCell>
                                <TableCell align="left">Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {transactionDetail.map((transaction) => (
                                <TableRow key={transaction.transactionId}>
                                    <TableCell align="left">{transaction.createdDate}</TableCell>
                                    <TableCell>{transaction.transactionId ? transaction.transactionId : ' - '}</TableCell>
                                    <TableCell align="left">{transaction.indicatorFlag ? transaction.indicatorFlag : ' - '}</TableCell>
                                    <TableCell align="left">{transaction.type}</TableCell>
                                    <TableCell align="left">
                                        {transaction.transactionAmount ? '$ ' + transaction.transactionAmount.toFixed(2) : ' - '}
                                    </TableCell>
                                    <TableCell align="left">
                                        {transaction.authorizationMessage ? transaction.authorizationMessage : ' - '}
                                    </TableCell>
                                    <TableCell align="left">{transaction.paymentNumber}</TableCell>
                                    <TableCell align="left" style={{ maxWidth: '300px' }}>
                                        {getTransactionMsg(transaction)}
                                    </TableCell>
                                    <TableCell align="left">{showAddAdjustmentFeeBtn(transaction)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Grid>
            </Grid>

            <div style={{ width: '100%', marginTop: '30px' }}>
                <Button
                    variant="contained"
                    color="secondary"
                    size="small"
                    style={{ textTransform: 'none' }}
                    onClick={props.onClick}
                >
                    Back To Home
                </Button>
            </div>

            {showAdjustmentFeeDialog && selectedTransaction &&
                <AddAdjustmentFeeDialog
                    isOpen={showAdjustmentFeeDialog}
                    closeDialog={() => toggleAdjustmentFeeDialog(false)}
                    confirmAddFee={confirmAddFee}
                    transactionData={selectedTransaction}
                />
            }

            <PaymentConfirmation
                open={showConfirm}
                handleClose={closeConfirm}
                paymentRequest={{
                    displayAmount: feeRequest.adjustmentFee,
                    reason: feeRequest.reason
                }}
                memberDetails={props.memberDetails}
                accept={accept}
                selectedPaymentMode="AdjustmentFee"
            />
        </div>
    );
};

export default AddAdjustmentFee;
